import { useMemo, useState } from "react";
import { SortOrder } from "@common/constants";
import {
    TransactionSort,
    TransactionsQuerySortField,
} from "@common/schemas/transaction.schema";

export enum TransactionSortValue {
    DATE_ASC = "DATE_ASC",
    DATE_DESC = "DATE_DESC",
    DESCRIPTION_ASC = "DESCRIPTION_ASC",
    DESCRIPTION_DESC = "DESCRIPTION_DESC",
    BANK_DESCRIPTION_ASC = "BANK_DESCRIPTION_ASC",
    BANK_DESCRIPTION_DESC = "BANK_DESCRIPTION_DESC",
    CATEGORY_ASC = "CATEGORY_ASC",
    CATEGORY_DESC = "CATEGORY_DESC",
    ACCOUNT_ASC = "ACCOUNT_ASC",
    ACCOUNT_DESC = "ACCOUNT_DESC",
    LEDGER_ASC = "LEDGER_ASC",
    LEDGER_DESC = "LEDGER_DESC",
    AMOUNT_ASC = "AMOUNT_ASC",
    AMOUNT_DESC = "AMOUNT_DESC",
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    CUSTOMER_ASC = "CUSTOMER_ASC",
    CUSTOMER_DESC = "CUSTOMER_DESC",
    ORIGINAL_AMOUNT_ASC = "ORIGINAL_AMOUNT_ASC",
    ORIGINAL_AMOUNT_DESC = "ORIGINAL_AMOUNT_DESC",
    ACTIONS_ASC = "ACTIONS_ASC",
    ACTIONS_DESC = "ACTIONS_DESC",
    CLASS_ASC = "CLASS_ASC",
    CLASS_DESC = "CLASS_DESC",
    REVIEW_ASC = "REVIEW_ASC",
    REVIEW_DESC = "REVIEW_DESC",
    BILLABLE_EXPENSE_INVOICE_STATUS_ASC = "BILLABLE_EXPENSE_INVOICE_STATUS_ASC",
    BILLABLE_EXPENSE_INVOICE_STATUS_DESC = "BILLABLE_EXPENSE_INVOICE_STATUS_DESC",
    NONE = "NONE",
}

export const DEFAULT_SORT = TransactionSortValue.DATE_DESC;

export interface UseSort {
    currentSort: TransactionSortValue;
    handleSortChange: (value: TransactionSortValue) => void;
    sortExpression: TransactionSort | undefined;
}

export const getSortExpression = (
    sort: TransactionSortValue,
): TransactionSort | undefined => {
    switch (sort) {
        case TransactionSortValue.DATE_DESC:
            return {
                field: TransactionsQuerySortField.DATE,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.DATE_ASC:
            return {
                field: TransactionsQuerySortField.DATE,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.DESCRIPTION_DESC:
            return {
                field: TransactionsQuerySortField.DISPLAY_DESCRIPTION,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.DESCRIPTION_ASC:
            return {
                field: TransactionsQuerySortField.DISPLAY_DESCRIPTION,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.BANK_DESCRIPTION_DESC:
            return {
                field: TransactionsQuerySortField.BANK_DESCRIPTION,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.BANK_DESCRIPTION_ASC:
            return {
                field: TransactionsQuerySortField.BANK_DESCRIPTION,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.CATEGORY_DESC:
            return {
                field: TransactionsQuerySortField.CATEGORY,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.CATEGORY_ASC:
            return {
                field: TransactionsQuerySortField.CATEGORY,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.ACCOUNT_DESC:
            return {
                field: TransactionsQuerySortField.FINANCIAL_ACCOUNT,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.ACCOUNT_ASC:
            return {
                field: TransactionsQuerySortField.FINANCIAL_ACCOUNT,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.AMOUNT_DESC:
            return {
                field: TransactionsQuerySortField.AMOUNT,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.AMOUNT_ASC:
            return {
                field: TransactionsQuerySortField.AMOUNT,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.COUNTERPARTY_DESC:
            return {
                field: TransactionsQuerySortField.COUNTERPARTY,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.COUNTERPARTY_ASC:
            return {
                field: TransactionsQuerySortField.COUNTERPARTY,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.CUSTOMER_DESC:
            return {
                field: TransactionsQuerySortField.CUSTOMER,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.CUSTOMER_ASC:
            return {
                field: TransactionsQuerySortField.CUSTOMER,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.ORIGINAL_AMOUNT_DESC:
            return {
                field: TransactionsQuerySortField.ORIGINAL_AMOUNT,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.ORIGINAL_AMOUNT_ASC:
            return {
                field: TransactionsQuerySortField.ORIGINAL_AMOUNT,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.ACTIONS_DESC:
            return {
                field: TransactionsQuerySortField.REQUIRED_ACTIONS_COUNT,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.ACTIONS_ASC:
            return {
                field: TransactionsQuerySortField.REQUIRED_ACTIONS_COUNT,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.CLASS_ASC:
            return {
                field: TransactionsQuerySortField.CLASS,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.CLASS_DESC:
            return {
                field: TransactionsQuerySortField.CLASS,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.LEDGER_DESC:
            return {
                field: TransactionsQuerySortField.GENERAL_LEDGER,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.LEDGER_ASC:
            return {
                field: TransactionsQuerySortField.GENERAL_LEDGER,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.REVIEW_DESC:
            return {
                field: TransactionsQuerySortField.REVIEW,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.REVIEW_ASC:
            return {
                field: TransactionsQuerySortField.REVIEW,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.BILLABLE_EXPENSE_INVOICE_STATUS_DESC:
            return {
                field: TransactionsQuerySortField.BILLABLE_EXPENSE_INVOICE_STATUS,
                order: SortOrder.DESC,
            };
        case TransactionSortValue.BILLABLE_EXPENSE_INVOICE_STATUS_ASC:
            return {
                field: TransactionsQuerySortField.BILLABLE_EXPENSE_INVOICE_STATUS,
                order: SortOrder.ASC,
            };
        case TransactionSortValue.NONE:
            return undefined;
    }
};

export function useSort(
    defaultSort: TransactionSortValue = DEFAULT_SORT,
): UseSort {
    const [currentSort, setCurrentSort] =
        useState<TransactionSortValue>(defaultSort);

    const sortExpression: TransactionSort | undefined = useMemo(
        () => getSortExpression(currentSort),
        [currentSort],
    );

    return {
        currentSort,
        handleSortChange: setCurrentSort,
        sortExpression,
    };
}
